import type { ClientPlaygroundConfigOptions, MockConfigs } from '@gotti/test-utils';
import type {
  PlaygroundConfig,
  PlaygroundDefinition,
  PlaygroundEnvironmentDefinition,
  PlaygroundMode,
  PlaygroundReadyHandler,
  PlaygroundScenario,
  PlaygroundSetupHandler,
  PlaygroundStepContext,
} from '../types.js';
import { refuseContradictoryOffline } from './offline.js';
import { PAIR_KEYS, PAIR_SIDE_KEYS, validateKnownKeys } from './validate.js';

/** Marks a module as a pair. A registry symbol, so a duplicated module graph still recognises it. */
export const PLAYGROUND_PAIR = Symbol.for('gotti.playground.pair');

/** The half of a pair that differs by mode: how the world boots and what game handles steps get. */
export interface PlaygroundPairSide<ClientApi = unknown, ServerApi = unknown, TGame = unknown> {
  environment: PlaygroundEnvironmentDefinition;
  setup?: PlaygroundSetupHandler;
  ready?: PlaygroundReadyHandler<ClientApi>;
  game?: (ctx: PlaygroundStepContext<ClientApi, undefined>) => TGame | Promise<TGame>;
  /** Merged over the pair's shared `clientConfig`. */
  clientConfig?: ClientPlaygroundConfigOptions;
  /** Merged over the pair's shared `mockConfig`. */
  mockConfig?: MockConfigs;
}

/** A side inline, or a factory that imports it lazily — so a page never loads the other side's module, nor what only it imports. */
export type PlaygroundPairSideModule<ClientApi = unknown, ServerApi = unknown, TGame = unknown> =
  | PlaygroundPairSide<ClientApi, ServerApi, TGame>
  | (() => Promise<PlaygroundPairSide<ClientApi, ServerApi, TGame> | { default: PlaygroundPairSide<ClientApi, ServerApi, TGame> }>);

type SharedKeys = 'title' | 'description' | 'readyTimeoutMs' | 'clientConfig' | 'mockConfig' | 'preload' | 'entry' | 'globals' | 'stubs' | 'assetRoots' | 'allowRoots' | 'dedupe' | 'optimizeDeps' | 'gameHooks' | 'videoBlackLimit';

/** One scenario run against an online (server-authoritative) and an offline (client-authoritative) boot, registered as `<file>` and `<file>-offline`. */
export interface PlaygroundPairDefinition<ClientApi = unknown, ServerApi = unknown, TGame = unknown>
  extends Pick<PlaygroundDefinition<ClientApi, ServerApi, TGame>, SharedKeys> {
  scenario: PlaygroundScenario<ClientApi, TGame>;
  online: PlaygroundPairSideModule<ClientApi, ServerApi, TGame>;
  offline: PlaygroundPairSideModule<ClientApi, ServerApi, TGame>;
}

/** What `definePlaygroundPair` returns: the pair as declared, marked so the loader and the page recognise it. */
export interface PlaygroundPairModule<ClientApi = unknown, ServerApi = unknown, TGame = unknown> extends PlaygroundPairDefinition<ClientApi, ServerApi, TGame> {
  [PLAYGROUND_PAIR]: true;
}

/** Names a pair's variants: `<file>` and `<file>-offline`. */
export const PAIR_VARIANT_SUFFIX: Record<PlaygroundMode, string> = { online: '', offline: '-offline' };

/** Types a `*.playground.ts` pair export. Both variants run `scenario` by construction — there is no per-mode skip. */
export const definePlaygroundPair = <ClientApi = unknown, ServerApi = unknown, TGame = unknown>(
  pair: PlaygroundPairDefinition<ClientApi, ServerApi, TGame>,
): PlaygroundPairModule<ClientApi, ServerApi, TGame> => ({ ...pair, [PLAYGROUND_PAIR]: true });

// `any` game types: a pair whose `game` returns a concrete type is not assignable to `PlaygroundPairModule<unknown, unknown, unknown>`
// (a step's `ctx` is contravariant in TGame), and this guard must accept every pair.
/** Whether `value` is a pair made by `definePlaygroundPair` — in this module graph or any other. */
export const isPlaygroundPair = (value: unknown): value is PlaygroundPairModule<any, any, any> =>
  typeof value === 'object' && value !== null && (value as Record<symbol, unknown>)[PLAYGROUND_PAIR] === true;

/**
 * Loads one side (awaiting a lazy factory) and refuses one that is no side, that carries a key a side does not have
 * (judged first: a misspelt `environment` is named as the key it is), or that contradicts the mode it boots.
 */
const resolveSide = async <ClientApi, ServerApi, TGame>(
  side: PlaygroundPairSideModule<ClientApi, ServerApi, TGame>,
  variant: PlaygroundMode,
): Promise<PlaygroundPairSide<ClientApi, ServerApi, TGame>> => {
  const loaded = typeof side === 'function' ? await side() : side;
  const value = (loaded && typeof loaded === 'object' && 'default' in loaded ? loaded.default : loaded) as PlaygroundPairSide<ClientApi, ServerApi, TGame> | undefined;
  if (value && typeof value === 'object') validateKnownKeys(value, PAIR_SIDE_KEYS, `the ${variant} side of a playground pair`);
  if (!value || typeof value !== 'object' || !value.environment) {
    throw new Error(`Playground pair: the ${variant} side must be an object (or a factory resolving to one) with an \`environment\`.`);
  }
  if (variant === 'offline' && !value.environment.offline) throw new Error('Playground pair: the offline side must declare environment.offline (its initAreas).');
  if (variant === 'online' && value.environment.offline) throw new Error('Playground pair: the online side must not declare environment.offline.');
  return value;
};

/**
 * The definition one variant of a pair boots as: the shared fields plus that side's boot (`environment`,
 * `setup`, `ready`, `game`), its `clientConfig` and `mockConfig` merged over the shared ones field by
 * field. Only the variant's own side is loaded and judged. An offline side is refused here exactly as its
 * boot would refuse it (`serverAreas` not `[]`, a `joinGame.initialAreas` — the half's own, else that of
 * `options.config`, the config the pair is listed in — …), so a contradiction fails when the pair loads,
 * before any page is served. So does a key the pair, or that side, does not have.
 */
export const resolvePairVariant = async <ClientApi, ServerApi, TGame>(
  pair: PlaygroundPairModule<ClientApi, ServerApi, TGame>,
  variant: PlaygroundMode,
  options: { config?: Pick<PlaygroundConfig, 'clientConfig'> } = {},
): Promise<PlaygroundDefinition<ClientApi, ServerApi, TGame>> => {
  validateKnownKeys(pair, PAIR_KEYS, 'playground pair');
  const side = await resolveSide(pair[variant], variant);
  const { online: _online, offline: _offline, [PLAYGROUND_PAIR]: _marker, ...shared } = pair;
  const clientConfig = side.clientConfig ? { ...(shared.clientConfig ?? {}), ...side.clientConfig } : shared.clientConfig;
  // As at boot: the half's own joinGame, else the config's — the config's clientConfig is the layer a half's sits on.
  if (variant === 'offline') refuseContradictoryOffline(side.environment, clientConfig?.joinGame ?? options.config?.clientConfig?.joinGame);
  return {
    ...shared,
    environment: side.environment,
    setup: side.setup,
    ready: side.ready,
    game: side.game,
    clientConfig,
    mockConfig: { ...(shared.mockConfig ?? {}), ...(side.mockConfig ?? {}) },
  };
};
